import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { styled } from '@mui/material/styles'
import {
  type BasePuiDialogProps,
  moment,
  PuiDialog,
} from '@pbt/pbt-ui-components'

import {
  PrescriptionSwapReason,
  useSwapReactiveRxMutation,
} from '~/api/graphql/generated/types'
import FeatureToggle from '~/constants/featureToggle'
import { AdministrationCourseType } from '~/constants/prescription'
import {
  getSwapProductTrackingData,
  type SwapProductAnalyticsKey,
} from '~/constants/swapProductAnalytics'
import { TimelineEntryType } from '~/constants/timelineConstants'
import { getCurrentUserId } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { Prescription } from '~/types/entities/prescriptions'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

import { RhapConfirmCloseDialog } from '../RhapConfirmCloseDialog'
import {
  EditPrescriptionStep,
  type EditPrescriptionStepHandle,
} from './EditPrescriptionStep'
import { FinalizePrescriptionStep } from './finalizePrescription/FinalizePrescriptionStep'
import { SwapPatientStep } from './patientStep/SwapPatientStep'
import SwapProductStep from './SwapProductStep'
import { SwapProductDialogSteps } from './types'
import {
  convertPrescriptionToRxInputForSwap,
  sanitizePrescriptionForSwap,
} from './utils'

import styles from './SwapProductDialog.module.scss'

// Type for prescriptions in swap flow
type SwapPrescription = Prescription

// Dialog dimension configuration by step
const DIALOG_DIMENSIONS = {
  [SwapProductDialogSteps.EDIT_RX]: {
    width: '750px',
    maxWidth: '750px',
    height: '90vh',
  },
  [SwapProductDialogSteps.CHANGE_PRODUCT]: {
    width: '1000px',
    maxWidth: '1000px',
    height: '90vh',
  },
  [SwapProductDialogSteps.CHANGE_PATIENT]: {
    width: '800px',
    maxWidth: '800px',
    height: '60vh',
  },
  [SwapProductDialogSteps.FINALIZE]: {
    width: '800px',
    maxWidth: '800px',
    height: '90vh',
  },
} as const

const DIALOG_CONTENT_DIMENSIONS = {
  [SwapProductDialogSteps.FINALIZE]: {
    padding: '16px 24px',
  },
  default: {
    padding: '0',
  },
} as const

const StyledPuiDialog = styled(PuiDialog)`
  .MuiPaper-root {
    width: ${({ step }: { step: SwapProductDialogSteps }) =>
      DIALOG_DIMENSIONS[step].width};
    max-width: ${({ step }: { step: SwapProductDialogSteps }) =>
      DIALOG_DIMENSIONS[step].maxWidth};
    height: ${({ step }: { step: SwapProductDialogSteps }) =>
      DIALOG_DIMENSIONS[step].height};
    min-height: 500px;
    display: flex;
    flex-direction: column;
  }

  .MuiDialogContent-root {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: ${({ step }: { step: SwapProductDialogSteps }) =>
      step === SwapProductDialogSteps.FINALIZE
        ? DIALOG_CONTENT_DIMENSIONS[SwapProductDialogSteps.FINALIZE].padding
        : DIALOG_CONTENT_DIMENSIONS.default.padding};
  }
`
const RejectionReason: Record<
  Exclude<SwapProductDialogSteps, SwapProductDialogSteps.FINALIZE>,
  string
> = {
  [SwapProductDialogSteps.CHANGE_PRODUCT]: 'Wrong product',
  [SwapProductDialogSteps.CHANGE_PATIENT]: 'Wrong pet',
  [SwapProductDialogSteps.EDIT_RX]: 'Wrong count',
}

const SwapReasonMapping: Record<
  Exclude<SwapProductDialogSteps, SwapProductDialogSteps.FINALIZE>,
  PrescriptionSwapReason
> = {
  [SwapProductDialogSteps.CHANGE_PRODUCT]: PrescriptionSwapReason.WrongProduct,
  [SwapProductDialogSteps.CHANGE_PATIENT]: PrescriptionSwapReason.WrongPet,
  [SwapProductDialogSteps.EDIT_RX]: PrescriptionSwapReason.WrongStrength,
}

const TRACKING_ANALYTICS_KEYS: Record<string, string> = {
  [TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL]: 'SWAP_PRODUCT_SEARCH_CANCEL',
  [TrackingEvent.SWAP_PRODUCT_RX_FORM_CANCEL]: 'SWAP_PRODUCT_RX_FORM_CANCEL',
  [TrackingEvent.SWAP_PRODUCT_RX_FORM_APPROVE]: 'SWAP_PRODUCT_RX_FORM_APPROVE',
  [TrackingEvent.SWAP_PRODUCT_FINALIZE_CANCEL]: 'SWAP_PRODUCT_FINALIZE_CANCEL',
  [TrackingEvent.SWAP_COUNT_RX_FORM_CANCEL]: 'SWAP_COUNT_RX_FORM_CANCEL',
  [TrackingEvent.SWAP_COUNT_RX_FORM_APPROVE]: 'SWAP_COUNT_RX_FORM_APPROVE',
  [TrackingEvent.SWAP_COUNT_FINALIZE_CANCEL]: 'SWAP_COUNT_FINALIZE_CANCEL',
  [TrackingEvent.SWAP_PATIENT_SEARCH_CANCEL]: 'SWAP_PATIENT_SEARCH_CANCEL',
  [TrackingEvent.SWAP_PATIENT_RX_FORM_CANCEL]: 'SWAP_PATIENT_RX_FORM_CANCEL',
  [TrackingEvent.SWAP_PATIENT_RX_FORM_APPROVE]: 'SWAP_PATIENT_RX_FORM_APPROVE',
  [TrackingEvent.SWAP_PATIENT_FINALIZE_CANCEL]: 'SWAP_PATIENT_FINALIZE_CANCEL',
  [TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL_GO_BACK]:
    'SWAP_PRODUCT_SEARCH_CANCEL_GO_BACK',
  [TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL_CLOSE]:
    'SWAP_PRODUCT_SEARCH_CANCEL_CLOSE',
  [TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL_DISCARD]:
    'SWAP_PRODUCT_SEARCH_CANCEL_DISCARD',
  [TrackingEvent.SWAP_PRODUCT_FINALIZE_CONFIRM]:
    'SWAP_PRODUCT_FINALIZE_CONFIRM',
  [TrackingEvent.SWAP_COUNT_FINALIZE_CONFIRM]: 'SWAP_COUNT_FINALIZE_CONFIRM',
  [TrackingEvent.SWAP_PATIENT_FINALIZE_CONFIRM]:
    'SWAP_PATIENT_FINALIZE_CONFIRM',
}

const stepEventMap = {
  [SwapProductDialogSteps.CHANGE_PRODUCT]: {
    close: TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL,
    cancelClose: TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL_GO_BACK,
    cancelDialogClose: TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL_CLOSE,
    confirmClose: TrackingEvent.SWAP_PRODUCT_SEARCH_CANCEL_DISCARD,
  },
  [SwapProductDialogSteps.CHANGE_PATIENT]: {
    close: TrackingEvent.SWAP_PATIENT_SEARCH_CANCEL,
  },
  [SwapProductDialogSteps.EDIT_RX]: {
    close: {
      [SwapProductDialogSteps.CHANGE_PRODUCT]:
        TrackingEvent.SWAP_PRODUCT_RX_FORM_CANCEL,
      [SwapProductDialogSteps.EDIT_RX]: TrackingEvent.SWAP_COUNT_RX_FORM_CANCEL,
      [SwapProductDialogSteps.CHANGE_PATIENT]:
        TrackingEvent.SWAP_PATIENT_RX_FORM_CANCEL,
    },
    approve: {
      [SwapProductDialogSteps.CHANGE_PRODUCT]:
        TrackingEvent.SWAP_PRODUCT_RX_FORM_APPROVE,
      [SwapProductDialogSteps.EDIT_RX]:
        TrackingEvent.SWAP_COUNT_RX_FORM_APPROVE,
      [SwapProductDialogSteps.CHANGE_PATIENT]:
        TrackingEvent.SWAP_PATIENT_RX_FORM_APPROVE,
    },
  },
  [SwapProductDialogSteps.FINALIZE]: {
    close: {
      [SwapProductDialogSteps.CHANGE_PRODUCT]:
        TrackingEvent.SWAP_PRODUCT_FINALIZE_CANCEL,
      [SwapProductDialogSteps.EDIT_RX]:
        TrackingEvent.SWAP_COUNT_FINALIZE_CANCEL,
      [SwapProductDialogSteps.CHANGE_PATIENT]:
        TrackingEvent.SWAP_PATIENT_FINALIZE_CANCEL,
    },
    finalize: {
      [SwapProductDialogSteps.CHANGE_PRODUCT]:
        TrackingEvent.SWAP_PRODUCT_FINALIZE_CONFIRM,
      [SwapProductDialogSteps.EDIT_RX]:
        TrackingEvent.SWAP_COUNT_FINALIZE_CONFIRM,
      [SwapProductDialogSteps.CHANGE_PATIENT]:
        TrackingEvent.SWAP_PATIENT_FINALIZE_CONFIRM,
    },
  },
} as const

const trackSwapEvent = (
  initialPrescription: Prescription,
  currentStep: SwapProductDialogSteps,
  initialStep: SwapProductDialogSteps,
  key: string,
) => {
  const stepData = stepEventMap[currentStep]
  if (!stepData || !(key in stepData)) return

  const eventOrMap = (stepData as Record<string, unknown>)[key]
  const event =
    typeof eventOrMap === 'object'
      ? (eventOrMap as Partial<Record<SwapProductDialogSteps, TrackingEvent>>)[
          initialStep
        ]
      : (eventOrMap as TrackingEvent | undefined)

  if (!event) return

  const trackingKey = TRACKING_ANALYTICS_KEYS[event]
  if (!trackingKey) return

  trackEvent(
    event,
    getSwapProductTrackingData(
      trackingKey as SwapProductAnalyticsKey,
      initialPrescription,
    ),
  )
}

interface SwapProductDialogProps extends BasePuiDialogProps {
  initialPrescription: Prescription
  initialStep: Exclude<SwapProductDialogSteps, SwapProductDialogSteps.FINALIZE>
}

export function SwapProductDialog({
  initialPrescription,
  initialStep,
  open,
  onClose,
}: SwapProductDialogProps) {
  const { t } = useTranslation('Dialogs')
  const navigate = useNavigate()
  const dialogRef = useRef<HTMLDivElement>(null)
  const editPrescriptionRef = useRef<EditPrescriptionStepHandle>(null)
  const [currentStep, setCurrentStep] =
    useState<SwapProductDialogSteps>(initialStep)
  const [previousStep, setPreviousStep] =
    useState<SwapProductDialogSteps | null>(null)
  const [currentPatientId, setCurrentPatientId] = useState(
    initialPrescription.patientId,
  )
  // Start with empty array - Active Rx prescriptions are added when approved
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([])
  const [editingPrescriptionIndex, setEditingPrescriptionIndex] =
    useState<number>(0)
  const [editStepKey, setEditStepKey] = useState<number>(0)

  // If starting at EDIT_RX step, create a new Active Rx from the Reactive Rx
  // Otherwise start with no pending prescription
  const [pendingPrescription, setPendingPrescription] =
    useState<Prescription | null>(
      initialStep === SwapProductDialogSteps.EDIT_RX
        ? sanitizePrescriptionForSwap(initialPrescription, {
            preserveVariationForCompounding: true,
          })
        : null,
    )
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState(false)
  const [swapReactiveRx, { loading: swapLoading, error: swapError }] =
    useSwapReactiveRxMutation()
  const [hasSwapError, setHasSwapError] = useState(false)
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )
  const currentUserId = useSelector(getCurrentUserId)
  const [isApproveButtonDisabled, setIsApproveButtonDisabled] = useState(false)

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const link = target.closest('a[href]')
      if (link && dialogRef.current?.contains(link)) {
        onClose?.()
      }
    }

    // Add click listener to capture link clicks within dialog
    document.addEventListener('click', handleLinkClick, true)

    return () => {
      document.removeEventListener('click', handleLinkClick, true)
    }
  }, [open, onClose])

  const handleClose = () => {
    trackSwapEvent(initialPrescription, currentStep, initialStep, 'close')
    setConfirmCloseDialogOpen(true)
  }

  const handleCancelClose = () => {
    trackSwapEvent(initialPrescription, currentStep, initialStep, 'cancelClose')
    setConfirmCloseDialogOpen(false)
  }

  const handleCancelDialogClose = () => {
    trackSwapEvent(
      initialPrescription,
      currentStep,
      initialStep,
      'cancelDialogClose',
    )
    setConfirmCloseDialogOpen(false)
  }

  const handleConfirmClose = () => {
    trackSwapEvent(
      initialPrescription,
      currentStep,
      initialStep,
      'confirmClose',
    )
    onClose?.()
  }

  const trackFinalize = () => {
    trackSwapEvent(initialPrescription, currentStep, initialStep, 'finalize')
  }

  const navigateToStep = (newStep: SwapProductDialogSteps) => {
    setPreviousStep(currentStep)
    setCurrentStep(newStep)
  }

  const editPrescriptionAtIndex = (index: number) => {
    setEditingPrescriptionIndex(index)
    setPendingPrescription(null) // Clear pending to use prescription from array
    setEditStepKey((prev) => prev + 1) // Force remount
    navigateToStep(SwapProductDialogSteps.EDIT_RX)
  }

  const deletePrescriptionAtIndex = (index: number) => {
    setPrescriptions((prev) => prev.filter((_, i) => i !== index))
  }

  const redirectToPatientRecords = () => {
    const { clientId } = initialPrescription
    const patientId = currentPatientId

    navigate(`/client/${clientId}/patient/${patientId}/records`, {
      state: {
        showSwapProductSuccess: true,
        applyFilters: [TimelineEntryType.PRESCRIPTION],
      },
    })
  }

  const handleFinalize = async () => {
    const approveActiveRxsPayload = prescriptions.map((prescription) => {
      // Strip all IDs - backend will generate real IDs when saving new Active Rx prescriptions
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { id: _id, ...prescriptionWithoutId } = prescription

      return convertPrescriptionToRxInputForSwap(
        prescriptionWithoutId as Prescription,
        isFoodCatalogEnabled,
        currentUserId,
      )
    })

    // Use doctor ID from first activeRx for declined ReactiveRx
    const doctorId = prescriptions[0]?.doctorId || initialPrescription.doctorId

    if (!doctorId) {
      // Guard against null doctorId to prevent backend validation errors
      setHasSwapError(true)
      return
    }

    // Set quantity refill expiration date to create date (without time) for declinedRx
    const createDateWithoutTime = initialPrescription.createdAt
      ? moment.utc(initialPrescription.createdAt).startOf('day').toISOString()
      : undefined

    const payload = {
      prescriptionId: initialPrescription.id,
      input: {
        swapReason: SwapReasonMapping[initialStep],
        declineReactiveRx: {
          ...convertPrescriptionToRxInputForSwap(
            {
              ...initialPrescription,
              doctorId,
              ...(createDateWithoutTime && {
                quantityRefillExpirationDate: createDateWithoutTime,
              }),
            },
            isFoodCatalogEnabled,
            currentUserId,
          ),
          rejectionReason: RejectionReason[initialStep],
        },
        approveActiveRxs: approveActiveRxsPayload,
      },
    }

    try {
      const result = await swapReactiveRx({
        variables: payload,
      })

      // Check for GraphQL errors or empty response
      if (result.errors || !result.data) {
        setHasSwapError(true)
        return
      }

      // Validate that prescriptions were actually created
      const createdPrescriptions =
        result.data.swapReactiveRx?.createdPrescriptions
      if (!createdPrescriptions || createdPrescriptions.length === 0) {
        // Swap failed - no prescriptions were created
        setHasSwapError(true)
        return
      }

      // Success - redirect to patient records
      trackFinalize()
      onClose?.()
      redirectToPatientRecords()
    } catch (err) {
      setHasSwapError(true)
    }
  }

  const handleAddItem = () => {
    setIsAddingNew(true)
    setCurrentStep(SwapProductDialogSteps.CHANGE_PRODUCT)
  }

  const handleReturnToFinalize = () => {
    setIsAddingNew(false)
    setCurrentStep(SwapProductDialogSteps.FINALIZE)
  }

  const titleComponents: Record<SwapProductDialogSteps, string | undefined> = {
    [SwapProductDialogSteps.FINALIZE]: t('SWAP_PRODUCT_DIALOG.FINALIZE_TITLE'),
    [SwapProductDialogSteps.CHANGE_PRODUCT]: t(
      'SWAP_PRODUCT_DIALOG.CHANGE_PRODUCT_TITLE',
    ),
    [SwapProductDialogSteps.CHANGE_PATIENT]: t(
      'SWAP_PRODUCT_DIALOG.CHANGE_PATIENT_TITLE',
    ),
    [SwapProductDialogSteps.EDIT_RX]: undefined,
  }

  return (
    <>
      <StyledPuiDialog
        actions={
          currentStep === SwapProductDialogSteps.EDIT_RX ? (
            <div className={styles.actions}>
              <KibButtonNew
                disabled={isApproveButtonDisabled}
                onClick={() => {
                  trackSwapEvent(
                    initialPrescription,
                    currentStep,
                    initialStep,
                    'approve',
                  )
                  if (!editPrescriptionRef.current?.validate()) {
                    return
                  }

                  const updatedPrescription =
                    editPrescriptionRef.current?.getPrescription()
                  if (!updatedPrescription) {
                    return
                  }

                  // Only generate temp ID for new prescriptions (no existing ID)
                  // This makes isEdit = true when editing from finalize, preventing
                  // doctor/unit fields from being wiped by outsideSoap && !isEdit logic
                  const prescriptionId = updatedPrescription.id
                    ? updatedPrescription.id
                    : `temp-swap-${Date.now()}`

                  const approvedPrescription: SwapPrescription = {
                    ...updatedPrescription,
                    id: prescriptionId,
                  }

                  // Check if prescription already exists in array by ID
                  // If exists, UPDATE at that index. If not, ADD to array.
                  const existingIndex = prescriptions.findIndex(
                    (p) => p.id === approvedPrescription.id,
                  )

                  if (existingIndex === -1) {
                    // New prescription - add to array
                    setPrescriptions((prev) => [...prev, approvedPrescription])
                    setPendingPrescription(null)
                    setIsAddingNew(false)
                  } else {
                    // Existing prescription - update at index
                    setPrescriptions((prev) =>
                      prev.map((p, i) =>
                        i === existingIndex ? approvedPrescription : p,
                      ),
                    )
                  }

                  navigateToStep(SwapProductDialogSteps.FINALIZE)
                }}
              >
                Approve Prescription
              </KibButtonNew>
              {(previousStep === SwapProductDialogSteps.CHANGE_PRODUCT ||
                previousStep === SwapProductDialogSteps.CHANGE_PATIENT) && (
                <KibButtonNew
                  emphasis="secondary"
                  onClick={() => navigateToStep(previousStep)}
                >
                  Back
                </KibButtonNew>
              )}
            </div>
          ) : null
        }
        header={
          currentStep === SwapProductDialogSteps.EDIT_RX
            ? editPrescriptionRef.current?.getHeader()
            : undefined
        }
        open={open}
        ref={dialogRef}
        scroll="paper"
        step={currentStep}
        title={titleComponents[currentStep as SwapProductDialogSteps]}
        onClose={handleClose}
      >
        {(() => {
          switch (currentStep) {
            case SwapProductDialogSteps.FINALIZE:
              return (
                <FinalizePrescriptionStep
                  hasError={Boolean(swapError || hasSwapError)}
                  initialStep={initialStep}
                  isSwapLoading={swapLoading}
                  prescriptions={prescriptions}
                  onAddItem={handleAddItem}
                  onDeletePrescription={deletePrescriptionAtIndex}
                  onEditPrescription={editPrescriptionAtIndex}
                  onProceed={handleFinalize}
                />
              )
            case SwapProductDialogSteps.CHANGE_PRODUCT:
              return (
                <SwapProductStep
                  prescription={initialPrescription}
                  showAlert={!isAddingNew}
                  onBack={isAddingNew ? handleReturnToFinalize : undefined}
                  onProceed={(newPrescription: Prescription) => {
                    // Clear product-specific fields when changing products
                    setPendingPrescription({
                      ...newPrescription,
                      // Clear main quantity fields (displayed in Quantity Section)
                      totalQuantity: undefined,
                      totalQuantityUnitId: undefined,
                      totalQuantityCustomUnit: undefined,
                      quantityDrugExpirationDate: undefined,
                      // Clear administration fields
                      administrationQuantity: undefined,
                      administrationQuantityUnitId: undefined,
                      administrationQuantityCustomUnit: undefined,
                      administrationFrequencyId: undefined,
                      administrationFrequencyNotes: undefined,
                      administrationRouteId: undefined,
                      administrationCourseLength: 0,
                      administrationCourseType:
                        AdministrationCourseType.AS_NEEDED,
                      notes: undefined,
                    })
                    navigateToStep(SwapProductDialogSteps.EDIT_RX)
                  }}
                />
              )
            case SwapProductDialogSteps.CHANGE_PATIENT:
              return (
                <SwapPatientStep
                  prescription={initialPrescription}
                  onPatientChange={(patientId) => {
                    setCurrentPatientId(patientId)
                  }}
                  onProceed={(newPrescription) => {
                    // Store the new prescription temporarily, will add to array when approved
                    setPendingPrescription(newPrescription)
                    navigateToStep(SwapProductDialogSteps.EDIT_RX)
                  }}
                />
              )
            case SwapProductDialogSteps.EDIT_RX: {
              const prescriptionToEdit =
                (pendingPrescription ??
                  prescriptions[editingPrescriptionIndex]) ||
                sanitizePrescriptionForSwap(initialPrescription, {
                  preserveVariationForCompounding: true,
                })

              return (
                <EditPrescriptionStep
                  clientId={initialPrescription.clientId || ''}
                  key={`edit-rx-${editStepKey}`}
                  patientId={currentPatientId || ''}
                  prescription={prescriptionToEdit}
                  ref={editPrescriptionRef}
                  onApproveButtonDisabledChange={setIsApproveButtonDisabled}
                  onCancel={onClose}
                  onProceed={() =>
                    navigateToStep(SwapProductDialogSteps.FINALIZE)
                  }
                />
              )
            }
            default:
              return null
          }
        })()}
      </StyledPuiDialog>
      <RhapConfirmCloseDialog
        bodyText={t('SWAP_PRODUCT_CONFIRM_CLOSE_DIALOG.BODY_TEXT')}
        open={confirmCloseDialogOpen}
        onCancelClose={handleCancelClose}
        onClose={handleCancelDialogClose}
        onConfirmClose={handleConfirmClose}
      />
    </>
  )
}
